/**
 * Versioned wire contract for the live PDF-to-HTML surface (`/s/:id`).
 *
 * This module intentionally has no runtime dependencies. Producers that sit on
 * the hot path can import the constants and types without pulling Zod into a
 * Worker bundle; consumers that need runtime validation should import
 * `@okrapdf/schemas/live-html` instead.
 */

export const LIVE_HTML_CONTRACT_VERSION = "okra.live_html.v1" as const;
export const LIVE_HTML_CONTRACT_HEADER = "x-okra-live-html-version" as const;
/**
 * CSS-selector-safe server block ids. Keeping the alphabet deliberately small
 * means `#${id}` is portable across DOM, SSE, snapshot, and non-browser
 * consumers without CSS escaping rules.
 */
export const LIVE_HTML_BLOCK_ID_PATTERN = "^[A-Za-z][A-Za-z0-9_-]*$" as const;

export type LiveHtmlContractVersion = typeof LIVE_HTML_CONTRACT_VERSION;

interface LiveHtmlEventBase {
  version: LiveHtmlContractVersion;
  /** Monotonic event id. Must match the SSE `id:` field. */
  seq: number;
}

export interface LiveHtmlProgressEvent extends LiveHtmlEventBase {
  type: "progress";
  phase: string;
}

export interface LiveHtmlAppendEvent extends LiveHtmlEventBase {
  type: "append";
  /** Stable id carried by the single top-level element in `html`. */
  block_id: string;
  /** One complete, server-sanitized top-level HTML element. */
  html: string;
}

export interface LiveHtmlReplaceEvent extends LiveHtmlEventBase {
  type: "replace";
  /** CSS id selector for the element replaced by `html`. */
  target: `#${string}`;
  /** One complete, server-sanitized top-level HTML element. */
  html: string;
}

export interface LiveHtmlDoneEvent extends LiveHtmlEventBase {
  type: "done";
}

export interface LiveHtmlErrorEvent extends LiveHtmlEventBase {
  type: "error";
  message: string;
}

export type LiveHtmlEvent =
  | LiveHtmlProgressEvent
  | LiveHtmlAppendEvent
  | LiveHtmlReplaceEvent
  | LiveHtmlDoneEvent
  | LiveHtmlErrorEvent;

export interface LiveHtmlSessionResource {
  object: "live_html_session";
  version: LiveHtmlContractVersion;
  id: string;
  status: "streaming";
  /** Same-origin HTML viewer URL. */
  url: `/s/${string}`;
  /** Same-origin SSE event URL. */
  events_url: `/s/${string}/events`;
}

// ── Compatibility surface consumed by @okrapdf/live-html-viewer (ported verbatim from the
// pre-consolidation contract file so both schema generations export one union). ──
/** Explicit opt-in media type for a typed session-creation receipt. */
export const LIVE_HTML_MEDIA_TYPE = "application/vnd.okrapdf.live-html+json" as const;

const liveHtmlBlockIdRegex = new RegExp(LIVE_HTML_BLOCK_ID_PATTERN);
const liveHtmlVoidTags = new Set([
  "area", "base", "br", "col", "embed", "hr", "img", "input", "link", "meta",
  "param", "source", "track", "wbr",
]);

/** Index just after a tag's `>`, ignoring `>` characters inside quoted attributes. */
function liveHtmlTagEnd(html: string, start: number): number {
  let quote: "\"" | "'" | undefined;
  for (let index = start + 1; index < html.length; index += 1) {
    const char = html[index];
    if (quote) {
      if (char === quote) quote = undefined;
      continue;
    }
    if (char === "\"" || char === "'") quote = char;
    else if (char === ">") return index + 1;
  }
  return -1;
}

/** Root id carried by a complete event fragment, if one is present. */
export function liveHtmlRootId(html: string): string | undefined {
  const normalized = html.trim();
  const end = liveHtmlTagEnd(normalized, 0);
  if (end === -1) return undefined;
  const openingTag = normalized.slice(0, end);
  const root = openingTag.match(/^<[a-zA-Z][a-zA-Z0-9-]*\b/);
  if (!root) return undefined;

  // Parse attributes rather than searching with `\bid`: that search also
  // matches `data-id` and id-looking text inside another quoted attribute.
  let cursor = root[0].length;
  while (cursor < openingTag.length) {
    while (/[\t\n\f\r ]/.test(openingTag[cursor] ?? "")) cursor += 1;
    if (openingTag[cursor] === ">" ||
      (openingTag[cursor] === "/" && openingTag[cursor + 1] === ">")) return undefined;

    const nameStart = cursor;
    while (cursor < openingTag.length &&
      !/[\t\n\f\r />=]/.test(openingTag[cursor])) cursor += 1;
    if (cursor === nameStart) return undefined;
    const name = openingTag.slice(nameStart, cursor).toLowerCase();
    while (/[\t\n\f\r ]/.test(openingTag[cursor] ?? "")) cursor += 1;

    let value: string | undefined;
    if (openingTag[cursor] === "=") {
      cursor += 1;
      while (/[\t\n\f\r ]/.test(openingTag[cursor] ?? "")) cursor += 1;
      const quote = openingTag[cursor];
      if (quote === "\"" || quote === "'") {
        const valueStart = ++cursor;
        while (cursor < openingTag.length && openingTag[cursor] !== quote) cursor += 1;
        if (cursor >= openingTag.length) return undefined;
        value = openingTag.slice(valueStart, cursor);
        cursor += 1;
      } else {
        const valueStart = cursor;
        while (cursor < openingTag.length &&
          !/[\t\n\f\r >]/.test(openingTag[cursor])) cursor += 1;
        value = openingTag.slice(valueStart, cursor);
      }
    }
    if (name === "id") return value || undefined;
  }
  return undefined;
}

/** Index after the first balanced top-level element, or -1 when incomplete. */
function liveHtmlElementEnd(html: string): number {
  const stack: string[] = [];
  let cursor = 0;
  let entered = false;
  while (cursor < html.length) {
    const start = html.indexOf("<", cursor);
    if (start === -1) return -1;

    if (html.startsWith("<!--", start)) {
      if (!entered) return -1;
      const commentEnd = html.indexOf("-->", start + 4);
      if (commentEnd === -1) return -1;
      cursor = commentEnd + 3;
      continue;
    }

    const end = liveHtmlTagEnd(html, start);
    if (end === -1) return -1;
    const token = html.slice(start, end);
    const closing = token.startsWith("</");
    const match = token.match(closing
      ? /^<\/([a-zA-Z][a-zA-Z0-9-]*)[\t\n\f\r ]*>$/
      : /^<([a-zA-Z][a-zA-Z0-9-]*)\b/);
    if (!match) return -1;
    const tag = match[1].toLowerCase();

    if (closing) {
      if (!entered || stack.at(-1) !== tag) return -1;
      stack.pop();
      if (stack.length === 0) return end;
    } else {
      if (!entered) {
        if (start !== 0) return -1;
        entered = true;
      }
      if (liveHtmlVoidTags.has(tag)) {
        if (stack.length === 0) return end;
      } else {
        stack.push(tag);
      }
    }
    cursor = end;
  }
  return -1;
}

/** True only for one complete top-level element with the addressed safe id. */
export function isSingleLiveHtmlBlock(html: string, expectedId: string): boolean {
  const normalized = html.trim();
  return liveHtmlBlockIdRegex.test(expectedId) &&
    liveHtmlElementEnd(normalized) === normalized.length &&
    liveHtmlRootId(normalized) === expectedId;
}

